/* eslint-disable node/prefer-global/process */
import type { Bid } from '@autobid/ui/types/Bid'
import type { UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useAllBidsSocket } from '@autobid/ui/composables/car/sockets/useAllBidsSocket'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

const CACHE_TIME = 1000 * 60 * 60

export const allBidsQueryKey = {
  all: () => ['all-bids'] as const,
  byCarId: (carId: number) => ['all-bids', carId] as const,
}

export function useAllBids(carId: Ref<number>, options?: UseQueryOptions<Bid[]>) {
  const { $trpc } = useNuxtApp()
  const push = usePush()
  const { t } = useI18n()
  const { init: initSockets } = useAllBidsSocket()

  const queryFn = async () => {
    try {
      const result = await $trpc.auctions.getAllBids.query({
        carId: unref(carId),
      })

      if (!result) {
        push.error(t('unexpected-error'))
        console.error('No response from server')
        return []
      }

      return result.data?.singleItem.item.itemData.bids || []
    }
    catch (error) {
      const { message, handled } = getError(error)

      if (!handled) {
        push.error(message)
      }

      return []
    }
  }

  onMounted(initSockets)

  return useQuery({
    queryKey: computed(() => allBidsQueryKey.byCarId(unref(carId))),
    queryFn,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
